import {observer} from "mobx-react-lite";
import {FormStepContainer} from "../components/decorations";
import {ReactComponent as NextSvg} from "../../assets/next.svg";
import {useCallback, useContext} from "react";
import {MladiVoznikStoreContext} from "./form_mladi_voznik";
import styled, {useTheme} from "styled-components";
import {StyledProps, Theme} from "../../theme";
import {CalendarDay, CvvCalendarGrid, CvvCalendarHeader} from "../components/calendar";
import {CvvLessonSlot, CvvMultidayLessonSlot} from "../components/slots";
import {MladiVoznikStore} from "../../stores/mladi_voznik_store";
import {TailSpin} from "react-loader-spinner"
import {zfill} from "../../utils";
import {Pricelist, PricelistItems} from "../pricelist";
import {ContentUnitFrameDark} from "../../components/dialogs";
import {CvvButton} from "../../components/buttons";

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 520px;
    padding: 32px 24px;
    margin: 20px auto 0;
    background: white;
    box-shadow: 0 0.25em 0.5em rgba(0, 45, 98, .15);
`;

const EmptyIconContainer = styled.div`
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: ${({theme}: StyledProps) => theme.shade};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
`;

const CalendarIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#002D62" strokeWidth="1.5"/>
        <path d="M3 9H21" stroke="#002D62" strokeWidth="1.5"/>
        <path d="M8 2V5" stroke="#002D62" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M16 2V5" stroke="#002D62" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="15" r="1.5" fill="#002D62"/>
    </svg>
);

const EmptyText = styled.div`
    color: ${({theme}: StyledProps) => theme.content};
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 8px;
`;

const ContactInfo = styled.div`
    color: ${({theme}: StyledProps) => theme.contentShade};
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 24px;

    a {
        color: ${({theme}: StyledProps) => theme.content};
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

export const FormMladiVoznikS2 = observer((props: any) => {
  const store = useContext(MladiVoznikStoreContext) as MladiVoznikStore;
  const theme = useTheme() as Theme;
  const buildDay = useCallback((date?: Date, payload?: any) => {
    const slotDate = store.form.step2?.date && new Date(store.form.step2!.date!.dateTime!.ts as number);
    const inPast = !!date && date.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000;
    const hasTecaj = !!date && !!store.step2.calendarData?.daysWithLectures?.includes(date!.getDate());
    return <CalendarDay
      key={date?.getTime() || Math.random()}
      selected={!!store.step2.selectedDay && store.step2.selectedDay.toDateString() === date?.toDateString()}
      slotSelected={!!slotDate && date?.toDateString() === slotDate.toDateString()}
      hint={hasTecaj}
      clickable={!!date && !inPast && hasTecaj}
      onClick={() => store.step2.setSelectedDay(date!)}>
      {date?.getDate()}
    </CalendarDay>
  }, [store.form.step2, store.step2]);

  const slots: any[] = [];
  if (store.step2.selectedDayData?.lectures) {
    for (const slot of store.step2.selectedDayData.lectures) {
      const date = new Date(slot.dateTime!.ts as number)
      const dates = slot.dateTimes?.map(d => new Date(d.ts as number));
      const hasMultipleDays = !!dates && dates.length > 1;
      const priceLabel = Pricelist.getValueEur(store.formStore?.pricelist?.items[PricelistItems.tecaj], store.pricelistModifiers) ?? '0,00€';
      const price = store.childrenMultiplier > 1 ? `${priceLabel} / otroka` : priceLabel;
      const isGratis = store.formStore?.meta?.isGratis ?? false;
      const slotContent = hasMultipleDays
        ? <CvvMultidayLessonSlot
          dates={dates!}
          isGratis={isGratis}
          price={price}
          selected={store.form.step2?.date?.id === slot.id}
          freePlaces={slot.freePlaces}
          location={store.config.s2?.showLocation ? slot.location : null}
        />
        : <CvvLessonSlot
          title={date.getHours() + '.' + zfill(date.getMinutes(), 2) + (isGratis ? '' : ` (${price})`)}
          selected={store.form.step2?.date?.id === slot.id}
          freePlaces={slot.freePlaces}
          location={store.config.s2?.showLocation ? slot.location : null}
        />;

      slots.push(<div
        onClick={() => store.step2.setDate(slot)}
        key={slot.id || date.getTime()} style={{marginTop: '8px', width: '80%'}}>
        {slotContent}
      </div>)
    }
  }

  // Show empty state when no termini available (determined from initial load)
  if (store.step2.noTerminiAvailable) {
    return <FormStepContainer style={{
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      paddingTop: '32px',
      paddingBottom: '50px',
    }}>
      <theme.textTheme.headline2.span>
        Izberite termin.
      </theme.textTheme.headline2.span>
      <EmptyStateContainer>
        <EmptyIconContainer>
          <CalendarIcon />
        </EmptyIconContainer>
        <EmptyText>
          Trenutno za program nimamo razpisanih terminov. Vabljeni, da se vseeno prijavite, mi pa vas o naslednjem terminu obvestimo.
        </EmptyText>
        <ContactInfo>
          Za več informacij nas pokličite na <a href="tel:080 26 36">080 26 36</a> ali pišite na <a href="mailto:cvv@amzs.si">cvv@amzs.si</a>.
        </ContactInfo>
        <CvvButton
          onClick={() => store.setStep(store.step + 1)}
          style={{width: 'auto', padding: '0 40px', justifyContent: 'flex-start'}}
          disabled={!store.stepValid(store.step, false)}>
          <theme.textTheme.headline5.span>NADALJUJ BREZ DATUMA</theme.textTheme.headline5.span>
          <NextSvg height={'22px'} style={{marginLeft: '16px'}}/>
        </CvvButton>
      </EmptyStateContainer>
    </FormStepContainer>
  }

  return <FormStepContainer style={{
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: '32px',
    paddingBottom: '50px',
  }}>
    <theme.textTheme.headline2.span>
      Izberite termin.
    </theme.textTheme.headline2.span>
    <div style={{
      margin: '20px auto 0 auto',
      textAlign: 'center'
    }}>
      <CvvCalendarHeader controller={store.step2.calendarController}/>
      <CvvCalendarGrid controller={store.step2.calendarController} dayBuilder={buildDay}/>
      {!store.step2.selectedDayData && store.step2.selectedDay &&
        <TailSpin color={theme.primary} height={80} width={80}/>}
      <theme.textTheme.error.span style={{
        opacity: (!store.ignoreEmptyValidations && !store.step2.done) ? 1 : 0,
        display: 'block',
        transition: 'opacity 300ms ease',
      }}>
        Izberite datum tečaja!
      </theme.textTheme.error.span>
    </div>
    {slots}

    {store.config.s2?.allowWithoutDate &&
      <CvvButton
        onClick={() => store.setStep(store.step + 1)}
        style={{marginTop: '16px', marginBottom: '8px'}}
        disabled={!store.stepValid(store.step, false)}>
        <theme.textTheme.headline5.span>NADALJUJ BREZ DATUMA</theme.textTheme.headline5.span>
        <NextSvg height={'22px'} style={{marginLeft: 'auto'}}/>
      </CvvButton>
    }

    {!store.formStore?.meta?.isGratis && store.step1.result?.jeClan && store.formStore?.pricelist?.getModifierDifference(PricelistItems.tecaj, Array.from(store.clanstvoModifiers)) !== 0 &&
      <ContentUnitFrameDark>
        <p style={{padding: '0', textAlign: 'center'}}>
          Ker ste član AMZS, se pri ceni upošteva popust v vrednosti <b>{
            store.formStore?.pricelist?.getModifierDifferenceEur(PricelistItems.tecaj, Array.from(store.clanstvoModifiers))
          }</b>.
        </p>
      </ContentUnitFrameDark>
    }

    {!store.formStore?.meta?.isGratis && store.step1.result?.jeClan && store.formStore?.pricelist?.getModifierDifference(PricelistItems.tecaj, Array.from(store.clanstvoModifiers)) === 0 &&
      <ContentUnitFrameDark>
        <p style={{padding: '0', textAlign: 'center'}}>Cena tega programa je zakonsko določena, zato vam ne moremo
          priznati članskega popusta. Pri komercialnih
          programih varne vožnje pa lahko člani AMZS izkoristite <b>do 30 % popusta.</b></p>
      </ContentUnitFrameDark>
    }
  </FormStepContainer>
})
